import { mapState } from "vuex";
import whitelabelMixin from "@/mixins/whitelabelMixin";

/**
 * The page name a component declares for itself, e.g. `title: "Forms"`.
 * Components declare only their own page name — the brand half of the title is
 * composed here so it lives in one place (ADO #187152).
 */
function getPageName(vm) {
  const { title } = vm.$options;
  if (!title) return undefined;
  return typeof title === "function" ? title.call(vm) : title;
}

/**
 * Registered globally via `Vue.mixin` in main.js, so this runs for every
 * component in the app. Only components that declare a `title` option set
 * document.title — without that guard every child component would overwrite
 * the title its page just set.
 *
 * That guard is also why `documentTitle` short-circuits before reading any
 * whitelabel state: components with no title never touch the store, keeping
 * this mixin inert for the vast majority of the component tree.
 *
 * The application name comes from whitelabelMixin rather than from state
 * directly, so tab titles use the host's whitelabel config (including
 * app.wewhistle.com) instead of a hardcoded brand.
 *
 * The first paint still happens before this mixin runs: `public/index.html`
 * and `main.js` both seed document.title to "Loading..." so the tab never
 * flashes the webpack default (package.json name "Whistle").
 */
export default {
  mixins: [whitelabelMixin],
  computed: {
    ...mapState({
      whitelabelReady: (state) => state.whitelabelReady,
    }),
    documentTitle() {
      const pageName = getPageName(this);
      if (!pageName) return undefined;
      if (!this.whitelabelReady) return "Loading...";
      return `${pageName} | ${this.applicationName}`;
    },
  },
  watch: {
    documentTitle: {
      immediate: true,
      handler(title) {
        if (title) document.title = title;
      },
    },
  },
};
